import { ACTION_VARIABLE_VALUES, IDatabaseLineItem, LineItemElementProps } from "../pages/CreatorTools/LineItemDBCreationTool/types";
import { IXactimateToolRowData } from "../shared/common/Azure";
import { getAllValidPermutations, getAllValidPermutationArrays } from "./getAllValidPermutations";
import { normalizeString } from "./utils";

export type MappingValidationResult = {
    goodMappings: IXactimateToolRowData[];
    invalidMappings: IXactimateToolRowData[];
    unmappedPermutations: string[];
};

const ACTION_VARIABLE_SET = new Set(ACTION_VARIABLE_VALUES.map((v) => v.toLowerCase()));

// Sentinel that replaces action-variable slot values when computing a masked key.
// Must not appear in any real permutation string.
const ACTION_VAR_SENTINEL = "\x00";

// ─── Shared helpers ───────────────────────────────────────────────────────────

const getActionVariableIndices = (elements: LineItemElementProps[]): Set<number> => {
    const indices = new Set<number>();
    elements.forEach((el, idx) => {
        if (
            el.type === "variable" &&
            el.acceptableValues &&
            el.acceptableValues.length > 0 &&
            el.acceptableValues.every((v) => ACTION_VARIABLE_SET.has(v.toLowerCase()))
        ) {
            indices.add(idx);
        }
    });
    return indices;
};

// Returns a map from normalized permutation string → its per-element value array.
// The values() of this map give every valid permutation array without a second traversal.
const buildPermArrayByNormalized = (elements: LineItemElementProps[]): Map<string, string[]> => {
    const map = new Map<string, string[]>();
    for (const arr of getAllValidPermutationArrays(elements)) {
        map.set(normalizeString(arr.join(" ")), arr);
    }
    return map;
};

const getMaskedKey = (arr: string[], actionVarIndices: Set<number>): string =>
    normalizeString(arr.map((v, i) => (actionVarIndices.has(i) ? ACTION_VAR_SENTINEL : v)).join(" "));

// ─── Validation ───────────────────────────────────────────────────────────────

export const validateXactimateMappings = (
    lineItem: IDatabaseLineItem,
    mappings: IXactimateToolRowData[]
): MappingValidationResult => {
    const validPerms = new Set(getAllValidPermutations(lineItem.elements));
    const coveredPerms = new Set<string>();
    const goodMappings: IXactimateToolRowData[] = [];
    const invalidMappings: IXactimateToolRowData[] = [];

    for (const mapping of mappings) {
        const normalized = normalizeString(mapping.acceptedLineString ?? "");
        if (validPerms.has(normalized)) {
            goodMappings.push(mapping);
            coveredPerms.add(normalized);
        } else {
            invalidMappings.push(mapping);
        }
    }

    // A mapping with no `activity` covers all action-variable variants of its
    // permutation string. Build a masked-key set to suppress those from unmapped.
    const actionVarIndices = getActionVariableIndices(lineItem.elements);
    const coveredMaskedKeys = new Set<string>();
    let permArrayByNormalized: Map<string, string[]> | null = null;

    if (actionVarIndices.size > 0) {
        permArrayByNormalized = buildPermArrayByNormalized(lineItem.elements);
        for (const mapping of goodMappings) {
            if (!mapping.activity) {
                const arr = permArrayByNormalized.get(normalizeString(mapping.acceptedLineString ?? ""));
                if (arr) coveredMaskedKeys.add(getMaskedKey(arr, actionVarIndices));
            }
        }
    }

    const unmappedPermutations = [...validPerms].filter((p) => {
        if (coveredPerms.has(p)) return false;
        if (coveredMaskedKeys.size > 0 && permArrayByNormalized) {
            const arr = permArrayByNormalized.get(p);
            if (arr && coveredMaskedKeys.has(getMaskedKey(arr, actionVarIndices))) return false;
        }
        return true;
    });

    return { goodMappings, invalidMappings, unmappedPermutations };
};

// ─── Good-mapping display groups ─────────────────────────────────────────────

export type GoodMappingDisplayItem =
    | { type: "individual"; mapping: IXactimateToolRowData }
    | {
          type: "grouped";
          representative: IXactimateToolRowData;
          mappings: IXactimateToolRowData[];
          coveredPermutations: string[];
      };

/**
 * Groups no-activity good mappings together with every action-variable
 * permutation they implicitly cover. Mappings with an explicit activity stay
 * individual — they only apply to their specific permutation string.
 */
export const buildGoodMappingGroups = (
    lineItem: IDatabaseLineItem,
    goodMappings: IXactimateToolRowData[]
): GoodMappingDisplayItem[] => {
    const actionVarIndices = getActionVariableIndices(lineItem.elements);

    if (actionVarIndices.size === 0) {
        return goodMappings.map((m) => ({ type: "individual", mapping: m }));
    }

    const permArrayByNormalized = buildPermArrayByNormalized(lineItem.elements);

    // Separate no-activity mappings (grouped) from activity-specific ones (individual)
    const byMaskedKey = new Map<string, IXactimateToolRowData[]>();
    const individualMappings: IXactimateToolRowData[] = [];

    for (const mapping of goodMappings) {
        if (mapping.activity) {
            individualMappings.push(mapping);
        } else {
            const arr = permArrayByNormalized.get(normalizeString(mapping.acceptedLineString ?? ""));
            if (!arr) {
                individualMappings.push(mapping);
                continue;
            }
            const masked = getMaskedKey(arr, actionVarIndices);
            const existing = byMaskedKey.get(masked);
            if (existing) existing.push(mapping);
            else byMaskedKey.set(masked, [mapping]);
        }
    }

    // Permutations that already have their own activity-specific mapping —
    // those are rendered as standalone individual cards, so we exclude them
    // from the consolidated group's covered list to avoid showing them twice.
    const individuallyMappedPerms = new Set(
        individualMappings.map((m) => normalizeString(m.acceptedLineString ?? "")),
    );

    // Collect every valid permutation string that belongs to each group
    const coveredPermsByMaskedKey = new Map<string, string[]>();
    for (const [normalized, arr] of permArrayByNormalized) {
        const masked = getMaskedKey(arr, actionVarIndices);
        if (!byMaskedKey.has(masked)) continue;
        if (individuallyMappedPerms.has(normalized)) continue;
        const list = coveredPermsByMaskedKey.get(masked);
        if (list) list.push(normalized);
        else coveredPermsByMaskedKey.set(masked, [normalized]);
    }

    const result: GoodMappingDisplayItem[] = [];
    for (const [masked, mappings] of byMaskedKey) {
        result.push({
            type: "grouped",
            representative: mappings[0],
            mappings,
            coveredPermutations: coveredPermsByMaskedKey.get(masked) ?? [],
        });
    }
    for (const mapping of individualMappings) {
        result.push({ type: "individual", mapping });
    }
    return result;
};

// ─── Unmapped permutation display groups ─────────────────────────────────────

export type UnmappedPermutationGroup =
    | { type: "individual"; perm: string }
    | { type: "grouped"; representative: string; perms: string[] };

/**
 * Groups unmapped permutations that differ only in their action-variable slot
 * so the UI can show them as one row (the representative) by default.
 */
export const buildUnmappedPermutationGroups = (
    lineItem: IDatabaseLineItem,
    unmappedPerms: string[]
): UnmappedPermutationGroup[] => {
    if (unmappedPerms.length === 0) return [];

    const actionVarIndices = getActionVariableIndices(lineItem.elements);

    if (actionVarIndices.size === 0) {
        return unmappedPerms.map((perm) => ({ type: "individual", perm }));
    }

    // Derive masked key for every perm from the shared map
    const permArrayByNormalized = buildPermArrayByNormalized(lineItem.elements);
    const maskedKeyByPerm = new Map<string, string>();
    for (const [normalized, arr] of permArrayByNormalized) {
        maskedKeyByPerm.set(normalized, getMaskedKey(arr, actionVarIndices));
    }

    // Group unmapped perms by masked key, preserving order of first appearance
    const byMaskedKey = new Map<string, string[]>();
    for (const perm of unmappedPerms) {
        const masked = maskedKeyByPerm.get(perm) ?? perm;
        const existing = byMaskedKey.get(masked);
        if (existing) existing.push(perm);
        else byMaskedKey.set(masked, [perm]);
    }

    const result: UnmappedPermutationGroup[] = [];
    const seen = new Set<string>();

    for (const perm of unmappedPerms) {
        if (seen.has(perm)) continue;
        const masked = maskedKeyByPerm.get(perm) ?? perm;
        const group = byMaskedKey.get(masked)!;
        for (const p of group) seen.add(p);
        result.push(
            group.length === 1
                ? { type: "individual", perm: group[0] }
                : { type: "grouped", representative: group[0], perms: group }
        );
    }

    return result;
};
